import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStoreClubAuth } from '../../contexts/StoreClubAuthContext';
import { QRCodeSVG } from 'qrcode.react';

const ClubCard: React.FC = () => {
  const { member, isAuthenticated, isLoading, refreshProfile } = useStoreClubAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!isLoading && !isAuthenticated) {
      navigate('/club/login');
    }
  }, [isLoading, isAuthenticated, navigate]);

  // Refresh profile to get latest member_code
  useEffect(() => {
    if (isAuthenticated && member && !member.member_code) {
      refreshProfile().catch(console.error);
    }
  }, [isAuthenticated, member, refreshProfile]);

  // Show loading state
  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.loadingText}>Loading...</div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.errorText}>Error: {error}</div>
          <button style={styles.retryButton} onClick={() => setError(null)}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated || !member) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.loadingText}>Please log in to view your card</div>
          <button
            style={styles.retryButton}
            onClick={() => navigate('/club/login')}
          >
            Log In
          </button>
        </div>
      </div>
    );
  }

  const memberCode = member.member_code || 'NO-CODE';
  const qrUrl = `https://app.redbridgefeed.com/store/checkin?code=${memberCode}`;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate('/club/dashboard')}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 style={styles.headerTitle}>My Card</h1>
        <div style={{ width: 40 }} />
      </div>

      {/* Card */}
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoArea}>
          <img src="/rbf-logo.png" alt="Red Bridge Farm Feed Co." style={styles.logoImage} />
        </div>

        {/* Member Name */}
        <div style={styles.memberName}>
          {member.name}
        </div>

        {/* QR Code */}
        <div style={styles.qrContainer}>
          {memberCode && memberCode !== 'NO-CODE' ? (
            <QRCodeSVG
              value={qrUrl}
              size={200}
              level="H"
              includeMargin={true}
            />
          ) : (
            <div style={styles.qrPlaceholder}>
              <div>No member code</div>
              <div style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>
                Contact support
              </div>
            </div>
          )}
        </div>

        {/* Member Code */}
        <div style={styles.memberCode}>{memberCode}</div>

        {/* Instructions */}
        <div style={styles.instructions}>
          Show this at checkout
        </div>
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <p style={styles.footerText}>
          Staff will scan your QR code to look up your account and record your purchases.
        </p>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1a1a1a',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
  },
  loadingText: {
    color: '#fff',
    fontSize: '1.125rem',
  },
  errorText: {
    color: '#f44336',
    fontSize: '1rem',
  },
  retryButton: {
    background: '#508591',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    cursor: 'pointer',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem',
    backgroundColor: '#1a1a1a',
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: '#fff',
    padding: '0.5rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    color: '#fff',
    fontSize: '1.125rem',
    fontWeight: 600,
    margin: 0,
  },
  card: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    paddingBottom: '4rem',
  },
  logoArea: {
    marginBottom: '2rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  logoImage: {
    width: '220px',
    height: 'auto',
  },
  memberName: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#fff',
    marginBottom: '2rem',
    textAlign: 'center',
  },
  qrContainer: {
    backgroundColor: '#fff',
    padding: '1rem',
    borderRadius: '16px',
    marginBottom: '1.5rem',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  },
  qrPlaceholder: {
    width: 200,
    height: 200,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#999',
    fontSize: '1rem',
    textAlign: 'center',
  },
  memberCode: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#fff',
    letterSpacing: '3px',
    fontFamily: 'monospace',
    marginBottom: '0.5rem',
  },
  instructions: {
    fontSize: '1rem',
    color: '#999',
    textAlign: 'center',
  },
  footer: {
    padding: '1.5rem 2rem',
    paddingBottom: '2rem',
  },
  footerText: {
    color: '#666',
    fontSize: '0.875rem',
    textAlign: 'center',
    margin: 0,
    lineHeight: 1.5,
  },
};

export default ClubCard;
